import z from 'zod';
import { useAuthCookie } from '~/composables/useAuthCookie';
import {
    COOKIE_NAME_DOCEBO_ACCESS_TOKEN,
    COOKIE_NAME_OAUTH2_ID_TOKEN,
    COOKIE_NAME_SW_CONTEXT_TOKEN
} from '~/types/cookieNames';
import { AUTHENTICATION_REFRESH_INTERVAL } from '~/utils/auth/constants';

const responseSchema = z.object({
    needsRefresh: z.boolean()
});

/**
 * The goal of this plugin is to periodically check if the user's
 * authentication session is still valid and refresh it if necessary.
 */
export default defineNuxtPlugin(() => {
    const { isKeycloakAuthenticated } = useLoginState();
    const { $shopwareApiClient } = useNuxtApp();
    const swContextTokenCookie = useAuthCookie(COOKIE_NAME_SW_CONTEXT_TOKEN);

    let interval: number | null = null;

    const syncClientAuthState = () => {
        refreshCookie(COOKIE_NAME_OAUTH2_ID_TOKEN);
        refreshCookie(COOKIE_NAME_SW_CONTEXT_TOKEN);
        refreshCookie(COOKIE_NAME_DOCEBO_ACCESS_TOKEN);

        $shopwareApiClient.defaultHeaders.apply({
            'sw-context-token': swContextTokenCookie.value ?? undefined
        });
    };

    const refresh = async () => {
        try {
            const response = await $fetch('/api/auth/me');

            const parsedResponse = responseSchema.parse(response);

            if (parsedResponse.needsRefresh) {
                await $fetch('/api/auth/refresh');
            }
        } catch (error) {
            console.error('Failed to refresh authentication session', error);
            stop();
        }

        syncClientAuthState();
    };

    const start = () => {
        interval ??= window.setInterval(refresh, AUTHENTICATION_REFRESH_INTERVAL);
    };

    const stop = () => {
        if (interval === null) return;

        window.clearInterval(interval);
        interval = null;
    };

    watchEffect(() => {
        if (isKeycloakAuthenticated.value) {
            start();
            return;
        }

        stop();
    });
});
